import { useState, useRef, useCallback, useEffect, type MutableRefObject } from 'react'
import { clsx } from 'clsx'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import PhotoUpload from './PhotoUpload'
import ReplyPreview from './ReplyPreview'
import EditPreview from './EditPreview'
import { RecordingWaveform } from './RecordingWaveform'
import { useChatStore } from '../../stores/chatStore'
import { wsManager } from '../../api/websocket'
import { useTypingMetrics, type TypingMetrics } from '../../hooks/useTypingMetrics'

interface ChatInputProps {
  onSendText: (content: string, typingMetrics?: TypingMetrics) => void
  onVoiceToggle?: () => void
  onVoiceCancel?: () => void // Cancel voice recording
  onVideoToggle?: () => void // Toggle video recording (same flow as voice)
  onVideoCancel?: () => void // Cancel video recording
  onPhotoUploaded?: () => void
  isRecording?: boolean
  isPending?: boolean // Waiting for mic permission
  isVideoRecording?: boolean
  isVideoPending?: boolean
  videoStreamRef?: MutableRefObject<MediaStream | null> // Live camera stream for preview
  frequencyData?: Uint8Array // Audio frequency data for waveform
  disabled?: boolean
  therapistType?: string
  placeholder?: string
}

// Live camera preview — connects MediaStream to <video> element
function VideoPreview({ streamRef }: { streamRef?: MutableRefObject<MediaStream | null> }) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoRef.current && streamRef?.current) {
      videoRef.current.srcObject = streamRef.current
    }
  }, [streamRef, streamRef?.current])

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      className="w-full h-full object-cover"
      style={{ transform: 'scaleX(-1)' }}
    />
  )
}

const DRAFT_STORAGE_KEY = 'jt_draft_message'
const DRAFT_SAVE_DELAY = 500 // ms

// Upload image from paste - use fetch to avoid axios Content-Type issues
async function uploadPastedImage(file: File) {
  const formData = new FormData()
  formData.append('file', file)

  // Get auth token from store
  const { useAuthStore } = await import('../../stores/authStore')
  const token = useAuthStore.getState().accessToken

  const response = await fetch('/api/v1/media/image/upload', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    body: formData,
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Upload failed: ${response.status} ${error}`)
  }

  return response.json()
}

export default function ChatInput({
  onSendText,
  onVoiceToggle,
  onVoiceCancel,
  onVideoToggle,
  onVideoCancel,
  onPhotoUploaded,
  isRecording = false,
  isPending = false,
  isVideoRecording = false,
  isVideoPending = false,
  videoStreamRef,
  frequencyData,
  disabled = false,
  therapistType = 'wendy',
  placeholder: placeholderProp,
}: ChatInputProps) {
  // Reply/Edit state from store
  const replyingTo = useChatStore((state) => state.replyingTo)
  const editingMessage = useChatStore((state) => state.editingMessage)
  const setEditingMessage = useChatStore((state) => state.setEditingMessage)
  const setReplyingTo = useChatStore((state) => state.setReplyingTo)
  const currentTherapist = useChatStore((state) => state.currentTherapist)
  const { recordKeystroke, getAndReset: getTypingMetrics } = useTypingMetrics()

  // Initialize message from localStorage draft
  const [message, setMessage] = useState(() => {
    try {
      return localStorage.getItem(DRAFT_STORAGE_KEY) || ''
    } catch {
      return ''
    }
  })

  // Pre-fill textarea when editing starts, clear on cancel (B7 fix)
  const wasEditingRef = useRef(false)
  useEffect(() => {
    if (editingMessage) {
      setMessage(editingMessage.content)
      textareaRef.current?.focus()
      wasEditingRef.current = true
    } else if (wasEditingRef.current) {
      setMessage('')
      wasEditingRef.current = false
    }
  }, [editingMessage])
  const [showPhotoUpload, setShowPhotoUpload] = useState(false)
  const [pastePreview, setPastePreview] = useState<string | null>(null)
  const [pastedFile, setPastedFile] = useState<File | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const queryClient = useQueryClient()

  // Mutation for pasted image upload
  const pasteUploadMutation = useMutation({
    mutationFn: uploadPastedImage,
    onSuccess: () => {
      setPastePreview(null)
      setPastedFile(null)
      // Refetch chat history to show the uploaded image and any response
      queryClient.invalidateQueries({ queryKey: ['chatHistory'] })
      onPhotoUploaded?.()
    },
    onError: (error) => {
      console.error('Paste upload failed:', error)
    },
  })

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto'
      // Set new height (capped by max-h in CSS)
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`
    }
  }, [])

  // Adjust height when message changes
  useEffect(() => {
    adjustTextareaHeight()
  }, [message, adjustTextareaHeight])

  // Save draft to localStorage (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        if (message.trim()) {
          localStorage.setItem(DRAFT_STORAGE_KEY, message)
        } else {
          localStorage.removeItem(DRAFT_STORAGE_KEY)
        }
      } catch {
        // localStorage not available
      }
    }, DRAFT_SAVE_DELAY)

    return () => clearTimeout(timer)
  }, [message])

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (message.trim() && !disabled) {
      // Collect typing metrics before sending
      const metrics = getTypingMetrics()

      // Check if editing a message
      if (editingMessage) {
        wsManager.editMessage(editingMessage.id, message)
        setEditingMessage(null)
        setReplyingTo(null) // Clear stale reply state
      }
      // Check if replying to a message
      else if (replyingTo) {
        // Get quoted text (first 100 chars of original message)
        const quotedText = replyingTo.content.slice(0, 100)
        wsManager.sendReply(message, replyingTo.id, quotedText)
      } else {
        onSendText(message, metrics)
      }
      setMessage('')
      // Clear draft from localStorage
      try {
        localStorage.removeItem(DRAFT_STORAGE_KEY)
      } catch {
        // localStorage not available
      }
      // Reset textarea height after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Track keystroke for typing metrics (uses ref, no re-renders)
    recordKeystroke(e)

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      // If paste preview is showing, upload the pasted image
      if (pastePreview && pastedFile) {
        handlePasteUpload()
      } else {
        handleSubmit()
      }
    }
  }

  // Handle paste for images
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (!file) continue

        // Validate size (10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert('Максимальный размер файла — 10 МБ')
          return
        }

        setPastedFile(file)

        // Create preview
        const reader = new FileReader()
        reader.onload = (ev) => setPastePreview(ev.target?.result as string)
        reader.readAsDataURL(file)
        return
      }
    }
  }, [])

  const handlePasteUpload = () => {
    if (pastedFile) {
      pasteUploadMutation.mutate(pastedFile)
    }
  }

  const clearPastePreview = () => {
    setPastePreview(null)
    setPastedFile(null)
  }

  const accentColor = {
    wendy: 'bg-wendy hover:bg-wendy-dark',
    artem: 'bg-artem hover:bg-artem-dark',
    finch: 'bg-finch hover:bg-finch-dark',
    una: 'bg-una hover:bg-una-dark',
  }[therapistType] || 'bg-primary-500 hover:bg-primary-600'

  const focusRing = {
    wendy: 'focus:ring-wendy/50',
    artem: 'focus:ring-artem/50',
    finch: 'focus:ring-finch/50',
    una: 'focus:ring-una/50',
  }[therapistType] || 'focus:ring-primary-500/50'

  return (
    <div className="max-w-2xl mx-auto px-4 py-3">
      {/* Edit preview (when editing a message) */}
      {editingMessage && (
        <EditPreview message={editingMessage} />
      )}

      {/* Reply preview (when replying to a message) */}
      {replyingTo && !editingMessage && (
        <ReplyPreview
          message={replyingTo}
          therapistName={currentTherapist?.name}
        />
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        {/* Audio (voice) button */}
        <button
          type="button"
          data-testid="voice-record-btn"
          onClick={onVoiceToggle}
          disabled={disabled || isPending || isVideoRecording}
          className={clsx(
            'flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center',
            'transition-colors duration-200',
            isRecording
              ? `${accentColor} text-white shadow-soft recording-pulse`
              : isPending
                ? 'bg-neutral-200 text-neutral-400'
                : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700',
            (disabled || isPending || isVideoRecording) && 'opacity-50 cursor-not-allowed'
          )}
          aria-label={isRecording ? 'Остановить запись' : isPending ? 'Ожидание...' : 'Голосовое сообщение'}
        >
          {isPending ? (
            // Loading spinner
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={2} opacity={0.25} />
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : isRecording ? (
            // Stop icon (square)
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
              <rect x="7" y="7" width="10" height="10" rx="1" fill="currentColor" />
            </svg>
          ) : (
            // Microphone icon
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          )}
        </button>

        {/* Photo button */}
        <button
          type="button"
          data-testid="photo-btn"
          onClick={() => setShowPhotoUpload(true)}
          disabled={disabled || isRecording || isVideoRecording}
          className={clsx(
            'flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center',
            'transition-colors duration-200',
            'bg-neutral-100 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700',
            (disabled || isRecording || isVideoRecording) && 'opacity-50 cursor-not-allowed'
          )}
          aria-label="Отправить фото"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
            />
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </button>

        {/* Video button — same toggle pattern as voice */}
        <button
          type="button"
          onClick={onVideoToggle}
          disabled={disabled || isRecording || isVideoPending}
          className={clsx(
            'flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center',
            'transition-colors duration-200',
            isVideoRecording
              ? `${accentColor} text-white shadow-soft recording-pulse`
              : isVideoPending
                ? 'bg-neutral-200 text-neutral-400'
                : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700',
            (disabled || isRecording || isVideoPending) && 'opacity-50 cursor-not-allowed'
          )}
          aria-label={isVideoRecording ? 'Остановить запись видео' : isVideoPending ? 'Ожидание камеры...' : 'Видеосообщение'}
        >
          {isVideoPending ? (
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={2} opacity={0.25} />
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : isVideoRecording ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
              <rect x="7" y="7" width="10" height="10" rx="1" fill="currentColor" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          )}
        </button>

        {/* Text input */}
        <textarea
          ref={textareaRef}
          data-testid="chat-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholderProp || 'Сообщение...'}
          disabled={disabled || isRecording || isVideoRecording}
          rows={1}
          className={clsx(
            'flex-1 px-4 py-2.5 bg-neutral-100 rounded-2xl resize-none min-h-[40px] max-h-[150px] overflow-y-auto',
            'transition-all duration-200',
            'placeholder:text-neutral-400',
            'focus:outline-none focus:ring-2 focus:bg-white',
            focusRing,
            (disabled || isRecording || isVideoRecording) && 'opacity-50 cursor-not-allowed'
          )}
        />

        {/* Send button */}
        <button
          type="submit"
          disabled={!message.trim() || disabled || isRecording || isVideoRecording}
          className={clsx(
            'flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center',
            'transition-all duration-200 active:scale-95',
            message.trim()
              ? `${accentColor} text-white shadow-soft`
              : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
          )}
          aria-label="Отправить"
          data-testid="send-button"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </form>

      {/* Recording indicator with waveform */}
      {(isRecording || isPending) && (
        <div className="mt-2 animate-fade-in">
          {isPending ? (
            <div className="flex items-center justify-center gap-3 px-4 py-2">
              <span className="inline-flex items-center gap-2 text-sm text-neutral-500">
                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                Запрос доступа к микрофону...
              </span>
              {/* Cancel button during pending */}
              <button
                type="button"
                onClick={onVoiceCancel}
                className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-full transition-colors"
                aria-label="Отмена"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3 px-4 py-2 bg-red-50 rounded-xl">
              {/* Cancel button */}
              <button
                type="button"
                onClick={onVoiceCancel}
                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Отмена"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
              {frequencyData && (
                <RecordingWaveform
                  frequencyData={frequencyData}
                  barCount={20}
                  minHeight={3}
                  maxHeight={20}
                />
              )}

              {/* Stop/send button */}
              <button
                type="button"
                onClick={onVoiceToggle}
                className="flex items-center gap-1.5 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-full transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Отправить
              </button>
            </div>
          )}
        </div>
      )}

      {/* Video recording indicator with camera preview */}
      {(isVideoRecording || isVideoPending) && (
        <div className="mt-2 animate-fade-in">
          {isVideoPending ? (
            <div className="flex items-center justify-center gap-3 px-4 py-2">
              <span className="inline-flex items-center gap-2 text-sm text-neutral-500">
                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                Запрос доступа к камере...
              </span>
              {/* Cancel button during pending */}
              <button
                type="button"
                onClick={onVideoCancel}
                className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-full transition-colors"
                aria-label="Отмена"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3 px-4 py-3">
              {/* Cancel button */}
              <button
                type="button"
                onClick={onVideoCancel}
                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Отмена"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Live camera preview — circular */}
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-red-400 animate-pulse bg-neutral-900">
                  <VideoPreview streamRef={videoStreamRef} />
                </div>
                {/* Recording dot */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse" />
              </div>

              {/* Stop/send button */}
              <button
                type="button"
                onClick={onVideoToggle}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-full transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Отправить
              </button>
            </div>
          )}
        </div>
      )}

      {/* Paste preview */}
      {pastePreview && (
        <div className="mt-3 p-3 bg-neutral-100 rounded-xl animate-fade-in">
          <div className="flex items-start gap-3">
            <img
              src={pastePreview}
              alt="Preview"
              className="w-20 h-20 object-cover rounded-lg"
            />
            <div className="flex-1">
              <p className="text-sm text-neutral-600 mb-2">
                Отправить изображение из буфера обмена?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handlePasteUpload}
                  disabled={pasteUploadMutation.isPending}
                  className={clsx(
                    'px-3 py-1.5 text-sm font-medium rounded-lg',
                    'bg-primary-500 text-white hover:bg-primary-600',
                    'transition-colors',
                    pasteUploadMutation.isPending && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {pasteUploadMutation.isPending ? 'Загрузка...' : 'Отправить'}
                </button>
                <button
                  onClick={clearPastePreview}
                  disabled={pasteUploadMutation.isPending}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg bg-neutral-200 text-neutral-700 hover:bg-neutral-300 transition-colors"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
          {pasteUploadMutation.isError && (
            <p className="mt-2 text-sm text-error">
              Ошибка загрузки. Попробуйте ещё раз.
            </p>
          )}
        </div>
      )}

      {/* Modals */}
      {showPhotoUpload && (
        <PhotoUpload
          onClose={() => setShowPhotoUpload(false)}
          onSuccess={onPhotoUploaded}
        />
      )}

    </div>
  )
}
